// Field controls of the inspector. A field definition gives get(el) and set(el, value); the control
// shows the value, applies changes live and records one history step when the user is done.
import { h, icon, clamp } from '../core/dom.js';
import { uiIcons, contentIcons } from './icons.js';
import { BREAKPOINTS, readColumn, effectiveColumn, setColumnSpan, equalizeRow, rebalanceRow, newColumnClasses, editingBreakpoint } from '../core/grid.js';
import { safeUrl, parseFragment } from '../core/sanitize.js';
import { BUTTON_STYLES, buttonStyle, setLink } from '../blocks/basic.js';
import { accordionItems } from '../blocks/components.js';
import { replaceClasses } from '../core/dom.js';
import { markBlock } from '../core/parser.js';
import { getBlock } from '../core/registry.js';

let fieldId = 0;

function row(label, control, help, t, wide = false) {
    fieldId += 1;
    const id = `pf-field-${fieldId}`;
    const focusable = control.matches && control.matches('input, select, textarea') ? control : control.querySelector && control.querySelector('input, select, textarea');
    if (focusable && !focusable.id) focusable.id = id;
    return h('div', { class: 'pf-field' + (wide ? ' pf-field-wide' : '') },
        label ? h('label', { class: 'pf-label', for: focusable ? focusable.id : null }, t(label)) : null,
        control,
        help ? h('div', { class: 'pf-help' }, t(help)) : null);
}

function translateOption(option, t) {
    const label = typeof option.label === 'string' && /^[a-z]+\.[A-Za-z0-9]+$/.test(option.label) ? t(option.label) : option.label;
    return option.suffix ? `${label}${option.suffix}` : label;
}

/**
 * Builds the control of a field. `api` = {t, editor, target, apply(fn, {commit, rerender})}.
 */
export function renderField(def, api) {
    const { t } = api;
    const el = api.target;
    const value = def.get ? def.get(el, api.editor.ctx) : null;
    const set = (next, options = {}, extra) => api.apply(target => def.set(target, next, api.editor.ctx, extra), options);
    const custom = FIELD_TYPES[def.type];
    if (custom) return custom(def, api, value, set);
    return null;
}

const FIELD_TYPES = {
    text(def, api, value, set) {
        const input = h('input', { type: 'text', class: 'pf-input', value: value || '', placeholder: def.placeholder ? api.t(def.placeholder) : '' });
        if (def.lazy) input.addEventListener('change', () => set(input.value, { commit: true }));
        else {
            input.addEventListener('input', () => set(input.value, { commit: false }));
            input.addEventListener('change', () => api.editor.commit());
        }
        return row(def.label, input, def.help, api.t);
    },

    url(def, api, value, set) {
        const input = h('input', { type: 'url', class: 'pf-input', value: value || '', placeholder: 'https://', spellcheck: 'false' });
        input.addEventListener('change', () => {
            const clean = safeUrl(input.value);
            input.classList.toggle('pf-invalid', input.value.trim() !== '' && clean === '');
            if (input.value.trim() === '' || clean) set(clean, { commit: true });
        });
        return row(def.label, input, def.help, api.t);
    },

    textarea(def, api, value, set) {
        const input = h('textarea', { class: 'pf-input pf-textarea', rows: def.rows || 3 }, value || '');
        input.addEventListener('input', () => set(input.value, { commit: false }));
        input.addEventListener('change', () => api.editor.commit());
        return row(def.label, input, def.help, api.t);
    },

    select(def, api, value, set) {
        const select = h('select', { class: 'pf-input pf-select' },
            def.options.map(option => h('option', { value: option.value, selected: option.value === value }, translateOption(option, api.t))));
        if (!def.options.some(option => option.value === value) && value) {
            select.append(h('option', { value, selected: true }, value));
        }
        select.addEventListener('change', () => set(select.value, { commit: true, rerender: true }));
        return row(def.label, select, def.help, api.t);
    },

    segmented(def, api, value, set) {
        const group = h('div', { class: 'pf-segmented' + (def.icons ? ' pf-segmented-icons' : ''), role: 'radiogroup' });
        def.options.forEach(option => {
            const label = translateOption(option, api.t);
            const btn = h('button', { type: 'button', class: 'pf-seg' + (option.value === value ? ' pf-on' : ''), role: 'radio', 'aria-checked': option.value === value ? 'true' : 'false', title: label, 'aria-label': label },
                def.icons && option.icon ? icon(uiIcons[option.icon]) : label);
            btn.addEventListener('click', () => set(option.value, { commit: true, rerender: true }));
            group.append(btn);
        });
        return row(def.label, group, def.help, api.t);
    },

    toggle(def, api, value, set) {
        const input = h('input', { type: 'checkbox', class: 'pf-switch-input', checked: !!value });
        const control = h('label', { class: 'pf-switch' }, input, h('span', { class: 'pf-switch-track', 'aria-hidden': 'true' }), h('span', { class: 'pf-switch-label' }, api.t(def.label)));
        input.addEventListener('change', () => set(input.checked, { commit: true, rerender: true }));
        return h('div', { class: 'pf-field pf-field-toggle' }, control, def.help ? h('div', { class: 'pf-help' }, api.t(def.help)) : null);
    },

    range(def, api, value, set) {
        const min = def.min ?? 0;
        const max = def.max ?? 100;
        const input = h('input', { type: 'range', class: 'pf-range', min, max, step: def.step || 1, value: clamp(Number(value) || min, min, max) });
        const number = h('input', { type: 'number', class: 'pf-input pf-number', min, max, step: def.step || 1, value: Number(value) || min, 'aria-label': api.t(def.label) });
        const unit = def.unit ? h('span', { class: 'pf-unit' }, def.unit) : null;
        input.addEventListener('input', () => {
            number.value = input.value;
            set(input.value, { commit: false });
        });
        input.addEventListener('change', () => api.editor.commit());
        number.addEventListener('change', () => {
            const next = clamp(Number(number.value) || min, min, max);
            number.value = next;
            input.value = next;
            set(String(next), { commit: true });
        });
        return row(def.label, h('div', { class: 'pf-range-row' }, input, number, unit), def.help, api.t);
    },

    color(def, api, value, set) {
        const input = h('input', { type: 'color', class: 'pf-color', value: value || '#000000' });
        const clear = h('button', { type: 'button', class: 'pf-icon-btn', title: api.t('action.clear'), 'aria-label': api.t('action.clear') }, icon(uiIcons.x));
        input.addEventListener('input', () => set(input.value, { commit: false }));
        input.addEventListener('change', () => api.editor.commit());
        clear.addEventListener('click', () => set('', { commit: true, rerender: true }));
        return row(def.label, h('div', { class: 'pf-color-row' }, input, h('code', { class: 'pf-color-value' }, value || api.t('option.none')), clear), def.help, api.t);
    },

    media(def, api, value, set) {
        const { t, editor } = api;
        const input = h('input', { type: 'text', class: 'pf-input', value: value && !value.startsWith('data:') ? value : '', placeholder: value && value.startsWith('data:') ? t('media.embedded') : 'https://', spellcheck: 'false' });
        const isImage = def.mediaType !== 'video';
        const preview = h('div', { class: 'pf-media-preview' + (value ? '' : ' pf-empty') });
        if (value && isImage) preview.append(h('img', { src: value, alt: '' }));
        else if (!value) preview.append(icon(uiIcons[isImage ? 'image' : 'video']));
        else preview.append(icon(uiIcons.video));
        const type = def.mediaType || 'image';
        const block = editor.selected;
        const busy = on => block && (on ? block.setAttribute('data-pf-uploading', '') : block.removeAttribute('data-pf-uploading'));
        const pick = async () => {
            const result = await editor.media.pick(type, { onStart: () => busy(true) });
            busy(false);
            if (result) set(result.url, { commit: true, rerender: true }, result);
        };
        const canPick = editor.media.canPick(type);
        preview.addEventListener('click', () => (canPick ? pick() : input.focus()));
        const browse = canPick
            ? h('button', { type: 'button', class: 'pf-btn pf-btn-primary' }, icon(uiIcons['upload-simple']), editor.media.hasLibrary() ? t('media.library') : t('media.upload'))
            : null;
        if (browse) browse.addEventListener('click', pick);
        input.addEventListener('change', () => {
            const clean = safeUrl(input.value, { image: isImage });
            input.classList.toggle('pf-invalid', input.value.trim() !== '' && !clean);
            if (input.value.trim() === '' || clean) set(clean, { commit: true, rerender: true });
        });
        // A file dropped on the field is uploaded (when files can be sent).
        const zone = h('div', { class: 'pf-media' }, preview, h('div', { class: 'pf-media-side' }, browse, input,
            canPick ? null : h('div', { class: 'pf-help' }, t('media.urlOnly'))));
        zone.addEventListener('dragover', event => {
            event.preventDefault();
            if (editor.media.canUpload(type)) zone.classList.add('pf-dragover');
        });
        zone.addEventListener('dragleave', () => zone.classList.remove('pf-dragover'));
        zone.addEventListener('drop', async event => {
            event.preventDefault();
            zone.classList.remove('pf-dragover');
            const file = event.dataTransfer.files && event.dataTransfer.files[0];
            if (!file) return;
            busy(true);
            const result = await editor.media.upload(file, type);
            busy(false);
            if (result) set(result.url, { commit: true, rerender: true }, result);
        });
        return row(def.label, zone, def.help, t, true);
    },

    link(def, api, value, set) {
        const { t } = api;
        const current = value || { url: '', newTab: false };
        const input = h('input', { type: 'text', class: 'pf-input', value: current.url, placeholder: 'https://', spellcheck: 'false' });
        const newTab = h('input', { type: 'checkbox', checked: current.newTab });
        const apply = () => {
            const clean = safeUrl(input.value);
            input.classList.toggle('pf-invalid', input.value.trim() !== '' && !clean);
            if (input.value.trim() !== '' && !clean) return;
            set({ url: clean, newTab: newTab.checked }, { commit: true });
        };
        input.addEventListener('change', apply);
        newTab.addEventListener('change', apply);
        return row(def.label, h('div', { class: 'pf-link-row' }, input, h('label', { class: 'pf-check' }, newTab, ' ', t('field.newTab'))), def.help, t, true);
    },

    icon(def, api, value, set) {
        const { t } = api;
        const fill = /-fill$/.test(value || '');
        const base = String(value || '').replace(/-fill$/, '');
        const search = h('input', { type: 'search', class: 'pf-input', placeholder: t('action.search'), 'aria-label': t('action.search') });
        const weight = h('input', { type: 'checkbox', checked: fill });
        const grid = h('div', { class: 'pf-icon-grid', role: 'listbox' });
        const names = Object.keys(contentIcons).filter(name => !name.endsWith('-fill'));
        const draw = () => {
            const query = search.value.trim().toLowerCase();
            grid.replaceChildren(...names.filter(name => !query || name.includes(query)).map(name => {
                const full = weight.checked ? `${name}-fill` : name;
                const btn = h('button', { type: 'button', class: 'pf-icon-choice' + (name === base ? ' pf-on' : ''), title: name, 'aria-label': name, role: 'option', 'aria-selected': name === base ? 'true' : 'false' }, icon(contentIcons[full]));
                btn.addEventListener('click', () => set(full, { commit: true, rerender: true }));
                return btn;
            }));
        };
        search.addEventListener('input', draw);
        weight.addEventListener('change', () => {
            draw();
            if (base) set(weight.checked ? `${base}-fill` : base, { commit: true });
        });
        draw();
        return row(def.label, h('div', { class: 'pf-icon-picker' }, h('div', { class: 'pf-icon-tools' }, search, h('label', { class: 'pf-check' }, weight, ' ', t('field.filled'))), grid), def.help, t, true);
    },

    /** Column width for each breakpoint; an empty size inherits the smaller one (shown greyed). */
    columns(def, api) {
        const { t, editor } = api;
        const el = api.target;
        const spec = readColumn(el);
        const current = editor.breakpoint();
        const grid = h('div', { class: 'pf-columns' });
        BREAKPOINTS.forEach((bp, index) => {
            const own = spec[bp.key];
            const inherited = index === 0 ? { value: '12' } : effectiveColumn(spec, BREAKPOINTS[index - 1].key);
            const inheritedLabel = inherited.value === 'fill' ? t('option.fill') : inherited.value === 'auto' ? t('option.auto') : `${inherited.value}/12`;
            const select = h('select', { class: 'pf-input pf-select' + (own === undefined ? ' pf-inherited' : ''), 'aria-label': `${t('field.width')} ${bp.label}` },
                h('option', { value: '' }, `${t('option.inherit')} (${inheritedLabel})`),
                ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(n => h('option', { value: n, selected: own === n }, `${n}/12`)),
                h('option', { value: 'fill', selected: own === 'fill' }, t('option.fill')),
                h('option', { value: 'auto', selected: own === 'auto' }, t('option.auto')));
            select.addEventListener('change', () => api.apply(target => setColumnSpan(target, bp.key, select.value), { commit: true, rerender: true }));
            const name = h('span', { class: 'pf-bp' + (bp.key === current ? ' pf-on' : ''), title: bp.min ? `≥ ${bp.min}px` : `< 576px` }, bp.label);
            grid.append(h('div', { class: 'pf-column-bp' }, name, select));
        });
        return row('field.columnWidth', grid, 'help.columnWidth', t, true);
    },

    'row-actions'(def, api) {
        const { t, editor } = api;
        const add = h('button', { type: 'button', class: 'pf-btn' }, icon(uiIcons.plus), t('action.addColumn'));
        add.addEventListener('click', () => api.apply(target => {
            const col = editor.ctx.h('div', { class: newColumnClasses(6) });
            target.append(col);
            markBlock(col, getBlock('col'), editor.ctx);
            if (!rebalanceRow(target)) equalizeRow(target, 'md');
        }, { commit: true, rerender: true }));
        const equal = h('button', { type: 'button', class: 'pf-btn' }, icon(uiIcons.columns), t('action.equalize'));
        equal.addEventListener('click', () => api.apply(target => equalizeRow(target, editingBreakpoint(editor.breakpoint()) || 'md'), { commit: true, rerender: true }));
        return h('div', { class: 'pf-field pf-actions-row' }, add, equal);
    },

    buttons(def, api) {
        const { t, editor } = api;
        const wrapper = def.target ? def.target(api.target) : api.target;
        if (!wrapper) return null;
        const list = h('div', { class: 'pf-items' });
        Array.from(wrapper.querySelectorAll(':scope > a')).forEach((a, index, all) => {
            const text = h('input', { type: 'text', class: 'pf-input', value: a.textContent.trim(), 'aria-label': t('field.text') });
            text.addEventListener('input', () => api.apply(() => {
                a.textContent = text.value;
            }, { commit: false }));
            text.addEventListener('change', () => editor.commit());
            const url = h('input', { type: 'text', class: 'pf-input', value: a.getAttribute('href') || '', placeholder: 'https://', 'aria-label': t('field.url'), spellcheck: 'false' });
            const newTab = h('input', { type: 'checkbox', checked: a.getAttribute('target') === '_blank' });
            const applyLink = () => {
                const clean = safeUrl(url.value);
                url.classList.toggle('pf-invalid', url.value.trim() !== '' && !clean);
                if (url.value.trim() !== '' && !clean) return;
                api.apply(() => setLink(a, url.value, newTab.checked), { commit: true });
            };
            url.addEventListener('change', applyLink);
            newTab.addEventListener('change', applyLink);
            const style = h('select', { class: 'pf-input pf-select', 'aria-label': t('field.style') },
                BUTTON_STYLES.map(value => h('option', { value, selected: buttonStyle(a) === value }, t(`button.${value.replace('btn-', '')}`))));
            style.addEventListener('change', () => api.apply(() => replaceClasses(a, /^btn-(?:outline-)?(?:primary|secondary|success|danger|warning|info|light|dark|link)$/, style.value), { commit: true }));
            const size = h('select', { class: 'pf-input pf-select', 'aria-label': t('field.size') },
                [['', 'option.medium'], ['btn-sm', 'option.small'], ['btn-lg', 'option.large']].map(([value, label]) => h('option', { value, selected: a.classList.contains(value) || (!value && !a.classList.contains('btn-sm') && !a.classList.contains('btn-lg')) }, t(label))));
            size.addEventListener('change', () => api.apply(() => replaceClasses(a, /^btn-(?:sm|lg)$/, size.value), { commit: true }));
            const remove = all.length > 1 && !def.single ? h('button', { type: 'button', class: 'pf-icon-btn pf-danger', title: t('action.remove'), 'aria-label': t('action.remove') }, icon(uiIcons.trash)) : null;
            if (remove) remove.addEventListener('click', () => api.apply(() => a.remove(), { commit: true, rerender: true }));
            list.append(h('div', { class: 'pf-item' },
                h('div', { class: 'pf-item-head' }, h('strong', {}, `${t('block.button')} ${all.length > 1 ? index + 1 : ''}`), remove),
                row('field.text', text, null, t), row('field.url', h('div', { class: 'pf-link-row' }, url, h('label', { class: 'pf-check' }, newTab, ' ', t('field.newTab'))), null, t),
                h('div', { class: 'pf-two' }, row('field.style', style, null, t), row('field.size', size, null, t))));
        });
        if (!def.single) {
            const add = h('button', { type: 'button', class: 'pf-btn' }, icon(uiIcons.plus), t('action.addButton'));
            add.addEventListener('click', () => api.apply(target => {
                const last = target.querySelector(':scope > a:last-of-type');
                const a = editor.ctx.h('a', { href: '#', class: 'btn btn-outline-primary', 'data-pf-edit': 'plain' }, t('placeholder.button'));
                if (last && last.classList.contains('ms-2') === false) a.classList.add('ms-2');
                target.append(a);
            }, { commit: true, rerender: true }));
            list.append(add);
        }
        return h('div', { class: 'pf-field pf-field-wide' }, list);
    },

    accordion(def, api) {
        const { t, editor } = api;
        const list = h('div', { class: 'pf-items' });
        const items = accordionItems(api.target);
        items.forEach((details, index) => {
            const summary = details.querySelector(':scope > summary');
            const input = h('input', { type: 'text', class: 'pf-input', value: summary ? summary.textContent.trim() : '', 'aria-label': t('field.question') });
            input.addEventListener('input', () => api.apply(() => {
                if (summary) summary.textContent = input.value;
            }, { commit: false }));
            input.addEventListener('change', () => editor.commit());
            const up = h('button', { type: 'button', class: 'pf-icon-btn', title: t('action.moveUp'), 'aria-label': t('action.moveUp'), disabled: index === 0 }, icon(uiIcons['arrow-up']));
            up.addEventListener('click', () => api.apply(() => details.previousElementSibling && details.previousElementSibling.before(details), { commit: true, rerender: true }));
            const remove = h('button', { type: 'button', class: 'pf-icon-btn pf-danger', title: t('action.remove'), 'aria-label': t('action.remove'), disabled: items.length === 1 }, icon(uiIcons.trash));
            remove.addEventListener('click', () => api.apply(() => details.remove(), { commit: true, rerender: true }));
            list.append(h('div', { class: 'pf-item pf-item-inline' }, input, up, remove));
        });
        const add = h('button', { type: 'button', class: 'pf-btn' }, icon(uiIcons.plus), t('action.addItem'));
        add.addEventListener('click', () => api.apply(target => {
            const last = target.querySelector(':scope > details:last-of-type');
            const item = last ? last.cloneNode(true) : editor.ctx.h('details', { class: 'border rounded-3 mb-2 px-3 py-2' }, editor.ctx.h('summary', { class: 'fw-semibold py-1' }), editor.ctx.h('div', { class: 'pt-2' }, editor.ctx.h('p')));
            item.querySelector('summary').textContent = `${t('placeholder.question')} ${items.length + 1}`;
            item.querySelector(':scope > div').innerHTML = `<p>${t('placeholder.answer')}</p>`;
            item.setAttribute('data-pf-open', '0');
            target.append(item);
            getBlock('accordion').prepare(target, editor.ctx);
        }, { commit: true, rerender: true }));
        list.append(add);
        return row('field.items', list, 'help.accordion', t, true);
    },

    html(def, api) {
        const { t, editor } = api;
        const el = api.target;
        const wrapped = el.classList.contains('pflow-html');
        const code = h('textarea', { class: 'pf-input pf-code', rows: 10, spellcheck: 'false' }, wrapped ? el.innerHTML.trim() : el.outerHTML);
        const apply = h('button', { type: 'button', class: 'pf-btn pf-btn-primary' }, t('action.apply'));
        apply.addEventListener('click', () => api.apply(target => {
            const fragment = parseFragment(code.value, editor.canvas.doc, editor.options.sanitize);
            if (wrapped) {
                target.replaceChildren(fragment);
                return target;
            }
            let first = fragment.firstElementChild;
            if (!first) return target;
            // Several elements, or text around them, stay together in one HTML block.
            if (fragment.childElementCount > 1 || Array.from(fragment.childNodes).some(node => node.nodeType === 3 && node.textContent.trim())) {
                first = editor.ctx.h('div', { class: 'pflow-html' });
                first.append(fragment);
                fragment.append(first);
            }
            first.setAttribute('data-pf', 'html');
            target.replaceWith(fragment);
            editor.selected = first;
            return first;
        }, { commit: true, rerender: true }));
        return row('field.code', h('div', { class: 'pf-code-field' }, code, apply), 'help.html', t, true);
    },
};
